'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CloseOutlined, QuestionOutlined } from '@zeelool/icon';
import { useCallback, useState } from 'react';
import type { Currency } from '@/api/types/public';

interface AfterpayMsgProps {
  afterpayPrice: string | number;
  afterImgShow?: boolean;
  currency?: Currency;
}

export default function AfterpayMsg({
  afterpayPrice,
  afterImgShow = false,
  currency,
}: AfterpayMsgProps) {
  const [showDialog, setShowDialog] = useState(false);
  const currentYear = new Date().getFullYear();

  const handleTapTips = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    // 阻止冒泡
    e.stopPropagation();
    e.preventDefault();
    setShowDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setShowDialog(false);
  }, []);

  return (
    <div className="flex items-center">
      <span className="text-[12px] md:text-14 text-auxiliary-text font-br mt-0 md:mt-4 ">
        <span>or 4 interest-free payments of </span>
        <span>{formatPriceNumberWithSymbol(String(afterpayPrice), currency)}</span>
        <QuestionOutlined
          className="text-14 cursor-pointer ml-4 inline-block"
          onClick={handleTapTips}
        />
      </span>
      {afterImgShow && <span className="text-14 text-text2 font-regular"> with </span>}
      {afterImgShow && (
        <Image
          src="/payment/afterpay.png"
          width={90}
          height={30}
          alt="afterpay"
          className="ml-11 mr-6"
        />
      )}
      <WebOnly>
        <Dialog
          open={showDialog}
          onClose={handleCloseDialog}
          footer={null}
          hideHeader
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: '775px',
              width: '775px',
            },
            '& .MuiDialogContent-root': {
              padding: 0,
            },
          }}
          title={null}
        >
          <div className="flex flex-col items-center bg-[#B2FCE4] w-full">
            <CloseOutlined
              className="text-34 cursor-pointer absolute top-20 right-20"
              onClick={handleCloseDialog}
            />
            <Image
              src="/payment/afterpay2.png"
              alt="afterpay"
              width={312}
              height={64}
              className="mt-[38px]"
            />
            <p className="mt-20 text-[44px] font-bb text-black leading-44 text-center">
              Shop now.Pay later.
              <br />
              Always interest-free
            </p>
            <Image
              src="/payment/afterpay3.png"
              alt="afterpay"
              width={775}
              height={150}
              className="mt-52 w-full"
            />
            <div className="w-full mt-26 pl-24 box-border flex justify-around">
              <div className="w-[148px] text-16 font-regular text-black leading-24 text-center">
                Add your favorites to cart
              </div>
              <div className="w-[125px] text-16 font-regular text-black leading-24 text-center">
                Select Afterpay at checkout
              </div>
              <div className="w-[159px] text-16 font-regular text-black leading-24 text-center">
                Log into or create your Afterpay account, instant approval decision
              </div>
              <div className="w-[162px] text-16 font-regular text-black leading-24 text-center">
                Your purchase will be split into 4 payments, payable every 2 weeks
              </div>
            </div>
            <p className="mt-52 pb-[42px] text-12 font-regular text-text1 w-[692px] leading-14 text-center">
              You must be over 18, a resident of the U.S. and meet additional eligibility criteria
              to qualify. Late fees may apply. Estimated payment amounts shown on product pages
              exclude taxes and shipping charges, which are added at checkout. Click here for
              complete terms. Loans to California residents made or arranged pursuant to a
              California Finance Lenders Law license. © {currentYear} Afterpay
            </p>
          </div>
        </Dialog>
      </WebOnly>

      <WapOnly>
        <Popup
          open={showDialog}
          onClose={handleCloseDialog}
          height="70vh"
          title={null}
          sx={{
            '& .MuiBox-root': {
              padding: 0,
              backgroundColor: '#B2FCE4',
            },
          }}
        >
          <div className="flex flex-col items-center bg-[#B2FCE4] w-full   p-[30px_33px_10px_33px]">
            <div className="flex flex-row items-center justify-between w-full">
              <Image src="/payment/afterpay2.png" alt="afterpay" width={156} height={32} />
              <CloseOutlined className="text-34 cursor-pointer" onClick={handleCloseDialog} />
            </div>
            <p className="mt-20 text-[28px] font-bb text-black leading-28 text-left w-full">
              Shop now.Pay later.
              <br />
              Always interest-free
            </p>
            <div className="flex flex-row items-center  mt-32">
              <Image src="/payment/afterpay3-mobile.png" alt="afterpay" width={45} height={100} />
              <div className="w-full pl-24 box-border flex flex-col  flex-1">
                <div className="h-[56px] text-14  font-br text-black leading-18 text-left  flex items-center">
                  Add your favorites to cart
                </div>
                <div className="h-[57px] text-14   font-br text-black leading-18 text-left flex items-center ">
                  Select Afterpay at checkout
                </div>
                <div className="h-[72px] text-14  font-br text-black leading-18 text-left flex items-center">
                  Log into or create your Afterpay account, instant approval decision
                </div>
                <div className="h-[56px] text-14  font-br text-black leading-18 text-left flex items-center">
                  Your purchase will be split into 4 payments, payable every 2 weeks
                </div>
              </div>
            </div>
            <p className="mt-52 pb-[42px] text-10 font-br text-text1  leading-14 text-center">
              You must be over 18, a resident of the U.S. and meet additional eligibility criteria
              to qualify. Late fees may apply. Estimated payment amounts shown on product pages
              exclude taxes and shipping charges, which are added at checkout. Click here for
              complete terms. Loans to California residents made or arranged pursuant to a
              California Finance Lenders Law license. © {currentYear} Afterpay
            </p>
          </div>
        </Popup>
      </WapOnly>
    </div>
  );
}
